// BISPowerBankCertification.jsx
import React from "react";

export default function BISPowerBankCertification() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            BIS Certification for Power Banks
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            BIS Certification for Lithium-ion Power Banks
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists manufacturers and importers with BIS
            Certification for Power Banks under IS 16046 (Part 2):2018 as per Quality
            Control Order (QCO) mandatory requirements for capacities 10Wh and above.
          </p>
          <p className="text-gray-700">
            Through BIS SMARK Scheme with Registration (RCMP), the process includes
            factory inspection, sample testing at BIS notified labs, and Standard Mark
            usage license for legal manufacturing, import, and sale across India.
          </p>
        </div>

        {/* Right: Power bank / lithium battery testing image */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/325153/pexels-photo-325153.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Lithium-ion battery testing for BIS Power Bank certification"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks provides end-to-end BIS RCM certification for
              power banks ensuring compliance with IS 16046 safety standards.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  BIS SMARK application preparation (Form VIII) through online portal
                  with ₹1 Lakh application fee and ₹4 Lakh testing charges.
                </li>
                <li>
                  Sample submission coordination to BIS notified labs (NTH, CPRI,
                  ERDA) for IS 16046 (Part 2) safety testing including thermal, short
                  circuit, and overcharge protection verification.
                </li>
                <li>
                  Factory audit preparation and WPC ETA compliance (for wireless
                  charging power banks) coordination with BIS Regional Offices.
                </li>
                <li>
                  License issuance (valid 2 years) and ongoing surveillance audit
                  support with Standard Mark label compliance guidance.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for BIS Power Bank Certification
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling technical documentation
              required for BIS RCM registration of lithium-ion power banks.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>Product technical specifications including capacity (Wh), voltage, cell chemistry, PCB protection circuit details.</li>
                <li>
                  Test reports from BIS notified labs confirming IS 16046 (Part 2)
                  compliance (cycle life, temperature cycling, crush test, vibration).
                </li>
                <li>
                  Manufacturing details (factory layout, process flow, quality
                  control plan) or import details (Foreign Manufacturer Certificate,
                  Free Sale Certificate).
                </li>
                <li>
                  Business documents (IEC, GST, incorporation certificate, lab test
                  plans, and label artwork with BIS Standard Mark compliance).
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm specializes in electronic product BIS compliance with proven
              success securing power bank RCM registrations across OEMs and importers.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Electronic Product Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Complete IS 16046 knowledge, BIS notified lab networks, and RCM
                  surveillance audit management for lithium-ion power banks.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Technical Documentation
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional test plan development, Form VIII preparation, and
                  factory documentation ensuring first-time BIS license approval.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Regulatory Support
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end lifecycle management from application through license
                  issuance, market surveillance, and 2-year renewal compliance.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Legally Sell Power Banks in India
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to secure BIS RCM Certification for
                your power banks and legally manufacture, import, and distribute across
                India's e-commerce platforms, retail chains, and consumer electronics market.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

